import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { locationsService } from '../../../services/locationsService';

const RevenueReportTab = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [dateFrom, setDateFrom] = useState(toISO(firstDay));
  const [dateTo, setDateTo] = useState(toISO(now));
  const [locationId, setLocationId] = useState(''); // '' = wszystkie
  const [groupBy, setGroupBy] = useState('month');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], totals: {}, count: 0 });
  const [estimation, setEstimation] = useState(null);
  const [estLoading, setEstLoading] = useState(false);

  useEffect(() => {
    locationsService.getLocations().then(res => {
      const arr = res?.data?.locations || res?.locations || res?.data || [];
      setLocations(Array.isArray(arr) ? arr : []);
    }).catch(err => console.error('Lokalizacje:', err));
  }, []);

  const buildRequestParams = useCallback((extra = {}) => {
    const p = { date_from: dateFrom, date_to: dateTo, group_by: groupBy, ...extra };
    if (locationId) {
      p.location_id = locationId;
    } else {
      p.location_id = 'all';
      p.all_locations = '1';
    }
    return p;
  }, [dateFrom, dateTo, locationId, groupBy]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/revenue-report', { params: buildRequestParams() });
      if (response.data?.success) {
        setData(response.data.data || { rows: [], totals: {}, count: 0 });
      } else {
        toast.error(response.data?.error || 'Błąd pobierania zestawienia');
      }
    } catch (err) {
      console.error('[Revenue]', err);
      toast.error(err?.message || 'Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  }, [buildRequestParams]);

  useEffect(() => { loadReport(); }, [loadReport]);

  /**
   * Estymacja biezacego miesiaca per sklep.
   * Osobne zapytanie (dzien po dniu, wszystkie lokalizacje, TYLKO biezacy miesiac),
   * bo karta ma zawsze prognozowac aktualny miesiac - niezaleznie od zakresu dat
   * ustawionego w filtrach powyzej. Zakres jest ograniczony do jednego miesiaca,
   * wiec to jedno lekkie zapytanie, nie obciazenie.
   *
   * Tempo liczymy z dni ZE SPRZEDAZA danego sklepu (nie kalendarzowych) - sklepy
   * moga miec rozne dni handlowe, a mnozenie przez pelny miesiac zawyzaloby prognoze.
   */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setEstLoading(true);
      try {
        const d = new Date();
        const y = d.getFullYear();
        const m = d.getMonth();
        const res = await api.get('/admin/revenue-report', {
          params: {
            date_from: toISO(new Date(y, m, 1)),
            date_to: toISO(d),
            group_by: 'day',
            location_id: 'all',
            all_locations: '1',
          },
        });
        if (cancelled) return;
        const rows = res.data?.data?.rows || [];
        const daysElapsed = d.getDate();
        const daysInMonth = new Date(y, m + 1, 0).getDate();

        const perLoc = new Map();
        rows.forEach((r) => {
          const key = r.location_id;
          if (!perLoc.has(key)) {
            perLoc.set(key, {
              location_id: key,
              nazwa: r.location_nazwa,
              obrot: 0, obrotBrutto: 0, marza: 0, koszt: 0, tx: 0, salesDays: 0,
            });
          }
          const acc = perLoc.get(key);
          const obrot = Number(r.obrot_netto_netto) || 0;
          acc.obrot += obrot;
          acc.obrotBrutto += Number(r.obrot_brutto_netto) || 0;
          acc.marza += Number(r.marza) || 0;
          acc.koszt += Number(r.koszt_towaru) || 0;
          acc.tx += Number(r.liczba_transakcji) || 0;
          if (obrot !== 0 || Number(r.liczba_transakcji) > 0) acc.salesDays += 1;
        });

        const list = Array.from(perLoc.values()).map((a) => {
          const tooFew = a.salesDays < 3;
          const projectedDays = tooFew
            ? a.salesDays
            : Math.max(a.salesDays, Math.round((a.salesDays / daysElapsed) * daysInMonth));
          const factor = a.salesDays > 0 ? projectedDays / a.salesDays : 0;
          const estObrot = a.obrot * factor;
          const estObrotBrutto = a.obrotBrutto * factor;
          const estMarza = a.marza * factor;
          return {
            ...a,
            tooFew,
            projectedDays,
            estObrot,
            estObrotBrutto,
            estMarza,
            estMarzaProc: estObrot > 0 ? (estMarza / estObrot) * 100 : 0,
            perDay: a.salesDays > 0 ? a.obrot / a.salesDays : 0,
          };
        }).sort((x, z) => z.estObrot - x.estObrot);

        setEstimation({
          list,
          daysElapsed,
          daysInMonth,
          monthLabel: `${String(m + 1).padStart(2, '0')}.${y}`,
          sumObrot: list.reduce((s, x) => s + x.estObrot, 0),
          sumObrotBrutto: list.reduce((s, x) => s + x.estObrotBrutto, 0),
          sumMarza: list.reduce((s, x) => s + x.estMarza, 0),
          sumObrotDotad: list.reduce((s, x) => s + x.obrot, 0),
          sumObrotBruttoDotad: list.reduce((s, x) => s + x.obrotBrutto, 0),
          sumMarzaDotad: list.reduce((s, x) => s + x.marza, 0),
        });
      } catch (err) {
        if (!cancelled) setEstimation(null);
      } finally {
        if (!cancelled) setEstLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quickRange = (type) => {
    const d = new Date();
    if (type === 'today') {
      setDateFrom(toISO(d)); setDateTo(toISO(d));
    } else if (type === 'month') {
      setDateFrom(toISO(new Date(d.getFullYear(), d.getMonth(), 1))); setDateTo(toISO(d));
    } else if (type === 'prev_month') {
      const f = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const t = new Date(d.getFullYear(), d.getMonth(), 0);
      setDateFrom(toISO(f)); setDateTo(toISO(t));
    } else if (type === 'year') {
      setDateFrom(toISO(new Date(d.getFullYear(), 0, 1))); setDateTo(toISO(d));
    } else if (type === 'prev_year') {
      setDateFrom(toISO(new Date(d.getFullYear() - 1, 0, 1)));
      setDateTo(toISO(new Date(d.getFullYear() - 1, 11, 31)));
    }
  };

  const downloadFile = async (format) => {
    try {
      const response = await api.get('/admin/revenue-report', {
        params: buildRequestParams({ format }),
        responseType: 'blob',
        timeout: 120000,
      });
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const locSuffix = locationId ? `_loc${locationId}` : '_all';
      a.download = `obrot_marza_${dateFrom}_${dateTo}${locSuffix}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Pobrano ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Błąd eksportu ${format.toUpperCase()}`);
    }
  };

  const totals = data.totals || {};
  const fmt = (n) => Number(n || 0).toFixed(2);

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>📈 Zestawienie obrotu i marży</h3>

      {/* Filtry */}
      <div style={{
        display: 'flex', gap: '1rem', alignItems: 'end', marginBottom: '1rem',
        padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: '4px',
        border: '1px solid var(--border)', flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Data od</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Data do</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Szybki zakres</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              ['today', 'Dziś'],
              ['month', 'Ten mies.'],
              ['prev_month', 'Poprz. mies.'],
              ['year', 'Rok'],
              ['prev_year', 'Poprz. rok'],
            ].map(([k, l]) => (
              <button key={k} onClick={() => quickRange(k)} style={{
                padding: '6px 12px', fontSize: '12px',
                border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer'
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Lokalizacja</label>
          <select value={locationId} onChange={e => setLocationId(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', maxWidth: '100%' }}>
            <option value="">Wszystkie lokalizacje</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.nazwa} ({l.kod_lokalizacji})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Grupowanie</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}>
            <option value="month">Miesiąc</option>
            <option value="day">Dzień</option>
          </select>
        </div>
        <button onClick={loadReport} disabled={loading} style={{
          padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--info)',
          color: '#fff', border: 'none', cursor: 'pointer'
        }}>🔄 Odśwież</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => downloadFile('csv')} disabled={loading || data.count === 0}
          style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--success)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1 }}>📊 CSV</button>
        <button onClick={() => downloadFile('pdf')} disabled={loading || data.count === 0}
          style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--danger)',
            color: '#fff', border: 'none',
            cursor: data.count === 0 ? 'not-allowed' : 'pointer',
            opacity: data.count === 0 ? 0.5 : 1 }}>📄 PDF</button>
      </div>

      {/* Karty totali */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem', marginBottom: '1rem'
      }}>
        {[
          { label: 'Obrót brutto (- zwroty)', val: totals.obrot_brutto_netto, color: 'var(--primary)' },
          { label: 'Obrót netto (- zwroty)', val: totals.obrot_netto_netto, color: 'var(--success)' },
          { label: 'VAT', val: totals.vat, color: 'var(--info)' },
          { label: 'Koszt towaru', val: totals.koszt_towaru, color: 'var(--warning)' },
          { label: 'Marża', val: totals.marza, color: 'var(--success)' },
          { label: 'Marża %', val: totals.marza_proc, color: 'var(--cyan)', suffix: '%' },
          { label: 'Średnia paragonu', val: totals.srednia_paragonu, color: 'var(--purple, #6f42c1)' },
          { label: 'Zwroty (brutto)', val: totals.zwroty_brutto, color: 'var(--danger)' },
          { label: 'Transakcji', val: totals.liczba_transakcji, color: 'var(--text-muted)', raw: true },
        ].map((c, i) => (
          <div key={i} style={{
            padding: '0.75rem', backgroundColor: 'var(--bg)', borderRadius: 4,
            border: '1px solid var(--border)', borderLeft: `3px solid ${c.color}`
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: c.color }}>
              {c.raw ? c.val : fmt(c.val)}{c.suffix || (c.raw ? '' : ' zł')}
            </div>
          </div>
        ))}
      </div>

      {/* Estymacja biezacego miesiaca per sklep */}
      <div style={{
        marginBottom: '1rem', border: '1px solid var(--border)', borderRadius: 4,
        borderLeft: '3px solid var(--purple, #6f42c1)', backgroundColor: 'var(--card-bg)'
      }}>
        <div style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: 'var(--purple, #6f42c1)' }}>
            🔮 Estymacja na koniec miesiąca ({estimation?.monthLabel || '—'}) — wszystkie sklepy
          </h4>
        </div>
        <div style={{ padding: '0.75rem' }}>
          {estLoading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Liczenie prognozy...</div>
          ) : !estimation || estimation.list.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Brak sprzedaży w tym miesiącu — nie ma z czego prognozować.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: '0.4rem', textAlign: 'left' }}>Sklep</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }} title="Dni ze sprzedażą / dni minione w miesiącu">Dni sprzed.</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót netto dotąd</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Śr. dziennie</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Prognoza obrotu netto</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Prognoza obrotu brutto</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża dotąd</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Prognoza marży</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimation.list.map((l) => (
                      <tr key={l.location_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.4rem', fontWeight: 600 }}>
                          {l.nazwa}
                          {l.tooFew && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>
                              za mało dni
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                          {l.salesDays}/{estimation.daysElapsed}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(l.obrot)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fmt(l.perDay)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, color: 'var(--info)' }}>
                          {l.tooFew ? '—' : fmt(l.estObrot)}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, color: 'var(--info)' }}>
                          {l.tooFew ? '—' : fmt(l.estObrotBrutto)}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(l.marza)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, color: l.estMarza >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                          {l.tooFew ? '—' : fmt(l.estMarza)}
                        </td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {l.tooFew ? '—' : `${fmt(l.estMarzaProc)}%`}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid var(--border)', backgroundColor: 'var(--bg)', fontWeight: 700 }}>
                      <td style={{ padding: '0.4rem' }}>RAZEM</td>
                      <td style={{ padding: '0.4rem' }}></td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(estimation.sumObrotDotad)}</td>
                      <td style={{ padding: '0.4rem' }}></td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--info)' }}>{fmt(estimation.sumObrot)}</td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--info)' }}>{fmt(estimation.sumObrotBrutto)}</td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(estimation.sumMarzaDotad)}</td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--success)' }}>{fmt(estimation.sumMarza)}</td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {estimation.sumObrot > 0 ? `${fmt((estimation.sumMarza / estimation.sumObrot) * 100)}%` : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Prognoza dotyczy zawsze bieżącego miesiąca — niezależnie od zakresu dat ustawionego w filtrach powyżej.
                Tempo liczone z dni ze sprzedażą danego sklepu i przenoszone na przewidywaną liczbę dni handlowych
                w miesiącu ({estimation.daysInMonth} dni kalendarzowych). Sklep z mniej niż 3 dniami sprzedaży
                nie dostaje prognozy — przy tak małej próbce byłaby zgadywaniem.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ładowanie...</div>
      ) : data.count === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Brak danych w wybranym okresie</div>
      ) : (
        <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Okres</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'left' }}>Lokalizacja</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Tx</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }} title="Średnia wartość paragonu (brutto / liczba transakcji)">Śr. paragon</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót brutto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>- Zwroty</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>= Brutto netto-zwroty</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót netto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>VAT</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Koszt</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża %</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem', fontWeight: 600 }}>{r.okres}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem' }}>{r.location_nazwa}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.liczba_transakcji}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>{fmt(r.srednia_paragonu)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.obrot_brutto)}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: r.zwroty_brutto > 0 ? 'var(--danger)' : 'inherit', whiteSpace: 'nowrap' }}>
                    {r.zwroty_brutto > 0 ? `-${fmt(r.zwroty_brutto)}` : '0.00'}
                  </td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(r.obrot_brutto_netto)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.obrot_netto_netto)}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.vat)}</td>
                  <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--warning)', whiteSpace: 'nowrap' }}>{fmt(r.koszt_towaru)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: r.marza > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                    {fmt(r.marza)}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {fmt(r.marza_proc)}%
                  </td>
                </tr>
              ))}
              {/* TOTAL row */}
              <tr style={{ borderTop: '2px solid var(--border)', backgroundColor: 'var(--bg)', fontWeight: 700 }}>
                <td style={{ padding: '0.5rem' }}>TOTAL</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem' }}>—</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{totals.liczba_transakcji}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>{fmt(totals.srednia_paragonu)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.obrot_brutto)}</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--danger)', whiteSpace: 'nowrap' }}>
                  -{fmt(totals.zwroty_brutto)}
                </td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.obrot_brutto_netto)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.obrot_netto_netto)}</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.vat)}</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--warning)', whiteSpace: 'nowrap' }}>{fmt(totals.koszt_towaru)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: totals.marza > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                  {fmt(totals.marza)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.marza_proc)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.75rem', fontSize: 11, color: 'var(--text-muted)' }}>
        Obrót netto-zwroty = obrót pomniejszony o zwroty/korekty z tego okresu.
        Marża = (obrót netto - zwroty netto) - koszt towaru (FIFO).
      </div>

      {/* Tabela per pracownik */}
      {Array.isArray(data.by_cashier) && data.by_cashier.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: 14 }}>👥 Średnia paragonu per pracownik</h4>
          <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>#</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Kasjer</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Liczba paragonów</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót brutto</th>
                  <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót netto</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Średnia paragonu</th>
                  <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>% średniej ogólnej</th>
                </tr>
              </thead>
              <tbody>
                {data.by_cashier.map((c, i) => {
                  const ogolna = totals.srednia_paragonu || 0;
                  const odchylenie = ogolna > 0 ? ((c.srednia_paragonu / ogolna) * 100 - 100) : 0;
                  const colorOdch = odchylenie > 5 ? 'var(--success)' : odchylenie < -5 ? 'var(--danger)' : 'var(--text-muted)';
                  return (
                    <tr key={c.kasjer_login} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{i + 1}</td>
                      <td style={{ padding: '0.5rem', fontWeight: 600 }}>{c.kasjer_login}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{c.liczba_transakcji}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(c.obrot_brutto)}</td>
                      <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(c.obrot_netto)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>
                        {fmt(c.srednia_paragonu)}
                      </td>
                      <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: colorOdch, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {odchylenie > 0 ? '+' : ''}{odchylenie.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: 11, color: 'var(--text-muted)' }}>
            % średniej ogólnej = o ile średnia paragonu kasjera odbiega od średniej całego zakresu.
            Zielone = powyżej średniej, czerwone = poniżej.
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueReportTab;
